import React from 'react';
import {
	CTAScreen,
	CustomHero,
	ImageTextSection,
	Reviews,
} from '@/components/landing/reusableSections';
import { Columns } from '@/components/landing/reusableSections/Columns';
import {
	useLandingShownEvent,
	useSendToAmplitudeScrolledPercent,
} from '@/hooks';
import { useTranslationWithLocale } from '@/hooks/useTranslationWithLocale';
import { GenerateLandingLinkService, GoogleService } from '@/services';
import {
	effectiveAboveList,
	forBusiness_jewelry_create,
	forBusiness_jewelry_effective,
	forBusiness_jewelry_enhance,
	forBusiness_jewelry_essential,
	forBusiness_jewelry_perfect,
	forBusiness_jewelry_showcase,
	reviewsLanding,
} from '@/data';
import {
	AMPLITUDE_EVENTS,
	GOOGLE_EVENTS,
	LANDING_AMPLITUDE_EVENTS,
	LOCAL_STORAGE_KEYS,
} from '@/constants';
import create from '@/public/images/for-business/jewelry/create.webp';
import effective from '@/public/images/for-business/jewelry/effective.webp';
import essential from '@/public/images/for-business/jewelry/essential.webp';
import imgHero from '@/public/images/for-business/jewelry/hero.webp';
import perfect from '@/public/images/for-business/jewelry/perfect.webp';
import showcase from '@/public/images/for-business/jewelry/showcase.webp';
import ctaScreen from '@/public/images/landing/ctaScreen.webp';
import styles from './JewelryLanding.module.scss';

interface Props {}
const JewelryLanding: React.FC<Props> = () => {
	const { t } = useTranslationWithLocale('jewelery-photography', 'en');
	const { t: tReviews } = useTranslationWithLocale('landing-reviews', 'en');

	const goToProduct = () => {
		GoogleService.logEvent(GOOGLE_EVENTS.LANDING_PASSED);
		window.location.href = GenerateLandingLinkService.webApp();
	};

	useSendToAmplitudeScrolledPercent(
		[50, 75, 100],
		LOCAL_STORAGE_KEYS.JEWELRY_IMAGES_SCROLLED_POINTS,
		AMPLITUDE_EVENTS.WEBAPP_LANDING_SCROLLED,
		{ type: LANDING_AMPLITUDE_EVENTS.JEWELRY_IMAGES_LANDING },
	);

	useLandingShownEvent({
		type: LANDING_AMPLITUDE_EVENTS.JEWELRY_IMAGES_LANDING,
	});
	return (
		<>
			<CustomHero
				image={imgHero}
				title={t('heroData_title')}
				subtitle={t('heroData_subtitle')}
				onClick={goToProduct}
				buttonText={t('getStarted')}
			/>

			{/* showcase */}
			<ImageTextSection
				title={t('showcaseData_mainTitle')}
				image={showcase}
				specialListItem
				data={forBusiness_jewelry_showcase}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
			/>

			{/*	essential */}
			<ImageTextSection
				title={t('essentialData_mainTitle')}
				subtitle1={t('essentialData_mainSubtitle1')}
				image={essential}
				data={forBusiness_jewelry_essential}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
				classes={{
					imageContainer: styles['jewelry__essential-image-container'],
					list: styles['jewelry__essential-list'],
					sectionTop: styles['jewelry__essential-section'],
				}}
			/>

			<Reviews t={tReviews} data={reviewsLanding} />

			{/* enhances */}
			<Columns
				t={t}
				title={t('enhancesData_mainTitle')}
				data={forBusiness_jewelry_enhance}
				buttonText={t('getStarted')}
				onClick={goToProduct}
			/>

			{/*	create */}
			<ImageTextSection
				title={t('createData_mainTitle')}
				image={create}
				data={forBusiness_jewelry_create}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
				classes={{
					section: styles['jewelry__create-section'],
				}}
			/>

			{/*	effective */}
			<ImageTextSection
				title={t('effectiveData_mainTitle')}
				image={effective}
				data={forBusiness_jewelry_effective}
				textsAboveList={effectiveAboveList}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
				classes={{
					imageContainer: styles['jewelry__effective-image-container'],
					list: styles['jewelry__effective-list'],
					section: styles['jewelry__effective-section'],
				}}
			/>

			{/*	perfect */}
			<ImageTextSection
				title={t('perfectData_mainTitle')}
				image={perfect}
				data={forBusiness_jewelry_perfect}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
			/>

			{/* TryLift */}
			<CTAScreen
				title1={t('tryLiftData_title1')}
				title_green={t('tryLiftData_greenText')}
				title2={t('tryLiftData_title2')}
				subtitle={t('tryLiftData_subtitle')}
				image={ctaScreen}
				altText={t('tryLiftData_altText')}
				buttonText={t('getStarted')}
				onClick={goToProduct}
				classes={{
					title: styles['jewelry__tryLift-title'],
					subtitleClass: styles['jewelry__tryLift-subtitle'],
				}}
			/>
		</>
	);
};

export default JewelryLanding;
